/**
 * Who moves on a plate, and how exactly (people, or any object):
 *
 *   ┌ the plate, each one found outlined and numbered ─────┐ ┌ tools ──────────────┐
 *   │  click one: it moves / stays still                     │ │ Pick · Add · Moves · │
 *   │  paint: add a person or object, or mend an outline     │ │ Stays still · Erase  │
 *   │  (or: the background behind them · the motion, live)   │ │ the list, Undo …    │
 *
 * The background behind whatever moves is filled in by the server (so
 * nothing looks cut out); when the brush makes more move than was filled,
 * it says so, and fills it on request. Brush marks apply as the brush lifts.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLockPageScroll } from '../../hooks/useLockPageScroll';
import { createPortal } from 'react-dom';
import {
  FiX, FiLoader, FiCheck, FiAlertTriangle, FiRotateCcw, FiPlus, FiTrash2, FiMousePointer, FiEdit2, FiSlash, FiDelete, FiEye, FiFilm, FiImage, FiSearch,
  FiZap, FiHexagon, FiSquare, FiCircle, FiPlusCircle, FiMinusCircle, FiMaximize2, FiMinimize2, FiTarget, FiUpload, FiZoomIn, FiZoomOut,
} from 'react-icons/fi';
import { magicWand, grow, shrink, snapEdges, countOf } from './selectionTools';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { showError } from '../FullStory/shared';
import { useNow, since } from './plateTime';
import { PlateLive } from './PlateLive';

const W = 960;
const H = 540;
const K = W / 1920; // design pixels → editor pixels
const TOOLS = [['pick', FiMousePointer], ['add', FiPlus], ['moves', FiEdit2], ['still', FiSlash], ['erase', FiDelete]];
// Exact selection, on the full-size picture (no AI): magic wand, polygon, rectangle, ellipse, brush to add / take away.
const SELECT_TOOLS = [['wand', FiZap], ['poly', FiHexagon], ['rect', FiSquare], ['ellipse', FiCircle], ['sadd', FiPlusCircle], ['ssub', FiMinusCircle]];
const isSelectTool = (tool) => SELECT_TOOLS.some(([id]) => id === tool);
const FW = 1920;
const FH = 1080;
const ZOOMS = [1, 1.5, 2, 3, 4];
// Each one's colour on the plate (moves); grey when it stays still.
const HUES = ['#22C55E', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6', '#14B8A6', '#EF4444', '#84CC16', '#06B6D4', '#F97316'];

const blank = () => Object.assign(document.createElement('canvas'), { width: W, height: H });
// Fetched as a file, then drawn: a picture from the server's address straight into
// a canvas would "taint" it, and its pixels (the outlines, the brush marks) couldn't be read.
const loadImage = async (src) => {
  const res = await fetch(src);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return createImageBitmap(await res.blob());
};

/** The brush's two layers (what moves, what stays) as the server takes them: grey, 255 moves, 128 stays. */
function paintPng(moves, still) {
  const m = moves.getContext('2d').getImageData(0, 0, W, H).data;
  const s = still.getContext('2d').getImageData(0, 0, W, H).data;
  const out = blank();
  const ctx = out.getContext('2d');
  const img = ctx.createImageData(W, H);
  let any = false;
  for (let i = 0; i < W * H; i++) {
    const v = m[i * 4 + 3] > 127 ? 255 : s[i * 4 + 3] > 127 ? 128 : 0;
    if (v) any = true;
    img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return any ? out.toDataURL('image/png') : null;
}

/** Marks over a person or object to add: white on black. */
function marksPng(marks) {
  const a = marks.getContext('2d').getImageData(0, 0, W, H).data;
  const out = blank();
  const ctx = out.getContext('2d');
  const img = ctx.createImageData(W, H);
  let any = false;
  for (let i = 0; i < W * H; i++) {
    const v = a[i * 4 + 3] > 40 ? 255 : 0;
    if (v) any = true;
    img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return any ? out.toDataURL('image/png') : null;
}

export const PeopleEditor = ({ plate, look, onClose, onChanged }) => {
  useLockPageScroll();
  const t = useT();
  const [data, setData] = useState(null);
  const [tool, setTool] = useState('pick');
  const [size, setSize] = useState(28);
  const [view, setView] = useState('plate'); // plate | behind | motion
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState('');
  const [marked, setMarked] = useState(false);
  const [mattes, setMattes] = useState({}); // id → { canvas (tinted), alpha (ImageData), x, y, w, h }
  const overlay = useRef(null);
  const layers = useRef({ moves: blank(), still: blank(), marks: blank() });
  const history = useRef([]);
  const drawing = useRef(null);
  // Exact selection: the picture's pixels (full size), the selection (0/255 per pixel), its undo steps.
  const picture = useRef(null);
  const selection = useRef(new Uint8Array(FW * FH));
  const selHistory = useRef([]);
  const [selCount, setSelCount] = useState(0);
  const [selMode, setSelMode] = useState('new');
  const [tolerance, setTolerance] = useState(18);
  const [contiguous, setContiguous] = useState(true);
  const [polygon, setPolygon] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [exactLabel, setExactLabel] = useState('');
  const [fillWay, setFillWay] = useState('extend');
  const [behindHint, setBehindHint] = useState('');
  const cleanFile = useRef(null);
  const selTint = useRef(null);
  const working = data?.task?.status === 'working';
  const now = useNow(working);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const img = await loadImage(api.plateTemplateUrl(plate.id, 'source.jpg', plate.updatedAt));
        const c = Object.assign(document.createElement('canvas'), { width: FW, height: FH });
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, FW, FH);
        if (!cancelled) picture.current = ctx.getImageData(0, 0, FW, FH);
      } catch { /* the wand and edge snapping wait for it */ }
    })();
    return () => { cancelled = true; };
  }, [plate.id, plate.updatedAt]);

  /** The selection as the overlay shows it (editor size): tinted, with a bright edge. */
  const tintSelection = useCallback(() => {
    const m = selection.current;
    const c = selTint.current || Object.assign(document.createElement('canvas'), { width: W, height: H });
    selTint.current = c;
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(W, H);
    const at = (x, y) => m[Math.min(FH - 1, y * 2) * FW + Math.min(FW - 1, x * 2)];
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const v = at(x, y);
        const edge = v !== at(Math.max(0, x - 1), y) || v !== at(Math.min(W - 1, x + 1), y) || v !== at(x, Math.max(0, y - 1)) || v !== at(x, Math.min(H - 1, y + 1));
        const k = (y * W + x) * 4;
        if (edge) { img.data[k] = 255; img.data[k + 1] = 255; img.data[k + 2] = 255; img.data[k + 3] = 255; } else if (v) { img.data[k] = 6; img.data[k + 1] = 182; img.data[k + 2] = 212; img.data[k + 3] = 110; }
      }
    }
    ctx.putImageData(img, 0, 0);
    setSelCount(countOf(m));
  }, []);

  /** A new shape (0/255, full size) into the selection: as the new selection, added, or taken away (Shift: add, Alt: take away). */
  const applyShape = useCallback((shape, e) => {
    const mode = e?.altKey ? 'sub' : e?.shiftKey ? 'add' : selMode;
    selHistory.current.push(Uint8Array.from(selection.current));
    if (selHistory.current.length > 15) selHistory.current.shift();
    const m = selection.current;
    if (mode === 'new') m.set(shape);
    else if (mode === 'add') { for (let i = 0; i < m.length; i++) if (shape[i]) m[i] = 255; } else for (let i = 0; i < m.length; i++) if (shape[i]) m[i] = 0;
    tintSelection();
  }, [selMode, tintSelection]);
  const setSelection = (next) => {
    selHistory.current.push(Uint8Array.from(selection.current));
    if (selHistory.current.length > 15) selHistory.current.shift();
    selection.current = next;
    tintSelection();
  };
  const undoSelection = () => {
    const last = selHistory.current.pop();
    if (!last) return;
    selection.current = last;
    tintSelection();
  };
  /** A shape drawn on a full-size canvas (draw(ctx) fills it white) as 0/255. */
  const rasterize = (draw) => {
    const c = Object.assign(document.createElement('canvas'), { width: FW, height: FH });
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#fff'; ctx.strokeStyle = '#fff';
    draw(ctx);
    const a = ctx.getImageData(0, 0, FW, FH).data;
    const out = new Uint8Array(FW * FH);
    for (let i = 0; i < out.length; i++) out[i] = a[i * 4 + 3] > 127 ? 255 : 0;
    return out;
  };
  /** The selection as the server takes it: a full-size PNG, white = selected. */
  const selectionPng = () => {
    const c = Object.assign(document.createElement('canvas'), { width: FW, height: FH });
    const ctx = c.getContext('2d');
    const img = ctx.createImageData(FW, FH);
    const m = selection.current;
    for (let i = 0; i < m.length; i++) { const v = m[i]; img.data[i * 4] = v; img.data[i * 4 + 1] = v; img.data[i * 4 + 2] = v; img.data[i * 4 + 3] = 255; }
    ctx.putImageData(img, 0, 0);
    return c.toDataURL('image/png');
  };

  const load = useCallback(async () => {
    try { setData(await api.platePeople(plate.id)); } catch (e) { showError(e); }
  }, [plate.id]);
  useEffect(() => { load(); }, [load]);

  // While a job runs (adding one, filling the background): ask now and then.
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(async () => {
      try {
        const next = await api.platePeople(plate.id);
        setData(next);
        if (!next.task) { onChanged?.(); layers.current.marks = blank(); setMarked(false); setLabel(''); }
      } catch { /* the next try */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [working, plate.id, onChanged]);

  // Each one's outline, tinted (and kept to know which one a click is on).
  const subjectsKey = JSON.stringify((data?.subjects || []).map((s) => [s.id, s.x, s.y, s.w, s.h]));
  useEffect(() => {
    if (!data?.subjects?.length) return undefined;
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(data.subjects.map(async (s, k) => {
        try {
          const img = await loadImage(api.plateTemplateUrl(plate.id, `subject-${s.id}.png`, s.id));
          const w = Math.max(1, Math.round(s.w * K)); const h = Math.max(1, Math.round(s.h * K));
          const c = Object.assign(document.createElement('canvas'), { width: w, height: h });
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const grey = ctx.getImageData(0, 0, w, h);
          const tinted = ctx.createImageData(w, h);
          const hue = HUES[k % HUES.length];
          const [r, g, b] = [1, 3, 5].map((j) => parseInt(hue.slice(j, j + 2), 16));
          for (let i = 0; i < w * h; i++) {
            tinted.data[i * 4] = r; tinted.data[i * 4 + 1] = g; tinted.data[i * 4 + 2] = b; tinted.data[i * 4 + 3] = grey.data[i * 4];
          }
          ctx.putImageData(tinted, 0, 0);
          // Where its number goes: the top of the outline itself (its box leaves room around it).
          let top = { x: w / 2, y: 0 };
          for (let y = 0, found = false; y < h && !found; y++) {
            let x0 = -1; let x1 = -1;
            for (let x = 0; x < w; x++) if (grey.data[(y * w + x) * 4] > 128) { if (x0 < 0) x0 = x; x1 = x; }
            if (x0 >= 0) { top = { x: (x0 + x1) / 2, y }; found = true; }
          }
          out[s.id] = { canvas: c, alpha: grey, x: s.x * K, y: s.y * K, w, h, hue, top };
        } catch { /* drawn without it */ }
      }));
      if (!cancelled) setMattes(out);
    })();
    return () => { cancelled = true; };
  }, [plate.id, subjectsKey]); // eslint-disable-line

  // The brush marks saved before: back into the two layers.
  useEffect(() => {
    if (!data?.painted) return;
    (async () => {
      try {
        const img = await loadImage(`${api.plateTemplateUrl(plate.id, 'people-paint.png', `p${data.rev}`)}`);
        const c = blank(); const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, W, H);
        const px = ctx.getImageData(0, 0, W, H).data;
        const moves = blank(); const still = blank();
        const mi = moves.getContext('2d').createImageData(W, H); const si = still.getContext('2d').createImageData(W, H);
        for (let i = 0; i < W * H; i++) {
          const v = px[i * 4];
          if (v > 190) { mi.data[i * 4 + 1] = 200; mi.data[i * 4 + 3] = 255; } else if (v > 60) { si.data[i * 4] = 230; si.data[i * 4 + 3] = 255; }
        }
        moves.getContext('2d').putImageData(mi, 0, 0); still.getContext('2d').putImageData(si, 0, 0);
        layers.current.moves = moves; layers.current.still = still;
        redraw();
      } catch { /* none */ }
    })();
  }, [data?.painted, plate.id]); // eslint-disable-line

  /** The overlay: outlines (moving: coloured; still: grey), the brush's marks, marks to add. */
  const redraw = useCallback(() => {
    const c = overlay.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    if (view === 'plate') {
      (data?.subjects || []).forEach((s) => {
        const m = mattes[s.id];
        if (!m) return;
        ctx.globalAlpha = s.selected ? 0.45 : 0.18;
        if (!s.selected) ctx.filter = 'grayscale(1)';
        ctx.drawImage(m.canvas, m.x, m.y, m.w, m.h);
        ctx.filter = 'none';
      });
      ctx.globalAlpha = 0.55;
      ctx.drawImage(layers.current.moves, 0, 0);
      ctx.drawImage(layers.current.still, 0, 0);
    }
    ctx.globalAlpha = 0.6;
    ctx.drawImage(layers.current.marks, 0, 0);
    ctx.globalAlpha = 1;
    // The exact selection, and a shape being made.
    if (view === 'plate' && selTint.current) ctx.drawImage(selTint.current, 0, 0);
    if (view === 'plate' && polygon.length) {
      ctx.strokeStyle = '#06B6D4'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4]);
      ctx.beginPath(); polygon.forEach(([x, y], k) => (k ? ctx.lineTo(x / 2, y / 2) : ctx.moveTo(x / 2, y / 2))); ctx.stroke(); ctx.setLineDash([]);
      polygon.forEach(([x, y], k) => { ctx.fillStyle = k ? '#fff' : '#06B6D4'; ctx.fillRect(x / 2 - 3, y / 2 - 3, 6, 6); });
    }
    // Numbers by each one.
    if (view === 'plate') {
      (data?.subjects || []).forEach((s, k) => {
        const m = mattes[s.id];
        const x = (m ? m.x + m.top.x : s.x * K) - 10; const y = (m ? m.y + m.top.y : s.y * K) + 4;
        ctx.fillStyle = s.selected ? (mattes[s.id]?.hue || '#22C55E') : '#6B7280';
        ctx.beginPath(); ctx.arc(x + 10, y + 10, 11, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(String(k + 1), x + 10, y + 11);
      });
    }
  }, [data, mattes, view, polygon, selCount]);
  useEffect(() => { redraw(); }, [redraw]);

  const at = (e) => {
    const r = overlay.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };

  const save = useCallback(async (change) => {
    setBusy('saving');
    try {
      setData(await api.updatePlatePeople(plate.id, change));
      onChanged?.();
    } catch (e) { showError(e); } finally { setBusy(''); }
  }, [plate.id, onChanged]);

  /** A click with "Pick": the one under it moves, or stays still. */
  const pick = (p) => {
    const hits = (data?.subjects || []).filter((s) => {
      const m = mattes[s.id];
      if (!m) return false;
      const x = Math.floor(p.x - m.x); const y = Math.floor(p.y - m.y);
      return x >= 0 && y >= 0 && x < m.w && y < m.h && m.alpha.data[(y * m.w + x) * 4] > 100;
    });
    if (!hits.length) return;
    // The smallest one there (one in front of a larger one).
    const s = hits.sort((a, b) => a.w * a.h - b.w * b.h)[0];
    toggle(s.id);
  };
  const toggle = (id) => {
    const selected = data.subjects.filter((s) => (s.id === id ? !s.selected : s.selected)).map((s) => s.id);
    setData((d) => ({ ...d, subjects: d.subjects.map((s) => ({ ...s, selected: selected.includes(s.id) })) }));
    save({ selected });
  };

  const snapshot = () => {
    const copy = (c) => { const n = blank(); n.getContext('2d').drawImage(c, 0, 0); return n; };
    history.current.push({ moves: copy(layers.current.moves), still: copy(layers.current.still), marks: copy(layers.current.marks) });
    if (history.current.length > 30) history.current.shift();
  };
  const stroke = (from, to) => {
    const target = tool === 'add' ? 'marks' : tool === 'still' ? 'still' : 'moves';
    const paint = (layer, colour, erase = false) => {
      const ctx = layers.current[layer].getContext('2d');
      ctx.globalCompositeOperation = erase ? 'destination-out' : 'source-over';
      ctx.strokeStyle = colour; ctx.lineWidth = size; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
    };
    if (tool === 'erase') { paint('moves', '#000', true); paint('still', '#000', true); paint('marks', '#000', true); } else {
      paint(target, target === 'marks' ? '#FDE047' : target === 'still' ? '#E11D48' : '#16A34A');
      // A part is either moving or still, not both.
      if (target === 'moves') paint('still', '#000', true);
      if (target === 'still') paint('moves', '#000', true);
    }
    redraw();
  };
  /** Where the pointer is on the full-size picture. */
  const atFull = (e) => { const p = at(e); return [Math.round(p.x * 2), Math.round(p.y * 2)]; };
  const shapeDrag = useRef(null);
  const down = (e) => {
    if (view === 'motion' || working) return;
    const p = at(e);
    if (tool === 'pick') { pick(p); return; }
    if (isSelectTool(tool)) {
      const [fx, fy] = atFull(e);
      if (tool === 'wand') {
        if (!picture.current) return;
        applyShape(magicWand(picture.current, fx, fy, tolerance, contiguous), e);
        return;
      }
      if (tool === 'poly') {
        // A click near the first point (or a double click) closes it.
        if (polygon.length > 2 && Math.hypot(fx - polygon[0][0], fy - polygon[0][1]) < 14 * (2 / zoom)) { closePolygon(e); return; }
        setPolygon((pts) => [...pts, [fx, fy]]);
        return;
      }
      e.currentTarget.setPointerCapture(e.pointerId);
      shapeDrag.current = { from: [fx, fy], to: [fx, fy], e: { shiftKey: e.shiftKey, altKey: e.altKey }, stroke: tool === 'sadd' || tool === 'ssub' ? [[fx, fy]] : null };
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    snapshot();
    drawing.current = p;
    stroke(p, p);
  };
  const move = (e) => {
    if (shapeDrag.current) {
      const d = shapeDrag.current;
      d.to = atFull(e);
      if (d.stroke) d.stroke.push(d.to);
      // The shape as it is dragged.
      redraw();
      const ctx = overlay.current.getContext('2d');
      ctx.save(); ctx.scale(0.5, 0.5); ctx.strokeStyle = tool === 'ssub' ? '#F43F5E' : '#06B6D4'; ctx.fillStyle = tool === 'ssub' ? 'rgba(244,63,94,0.3)' : 'rgba(6,182,212,0.3)';
      ctx.lineWidth = d.stroke ? size * 2 : 3; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath();
      if (d.stroke) { d.stroke.forEach(([x, y], k) => (k ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.globalAlpha = 0.5; ctx.stroke(); } else if (tool === 'rect') { ctx.rect(d.from[0], d.from[1], d.to[0] - d.from[0], d.to[1] - d.from[1]); ctx.fill(); ctx.stroke(); } else {
        ctx.ellipse((d.from[0] + d.to[0]) / 2, (d.from[1] + d.to[1]) / 2, Math.abs(d.to[0] - d.from[0]) / 2, Math.abs(d.to[1] - d.from[1]) / 2, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      }
      ctx.restore();
      return;
    }
    if (!drawing.current) return;
    const p = at(e);
    stroke(drawing.current, p);
    drawing.current = p;
  };
  const up = () => {
    if (shapeDrag.current) {
      const d = shapeDrag.current;
      shapeDrag.current = null;
      const [x0, y0] = d.from; const [x1, y1] = d.to;
      if (d.stroke) {
        const shape = rasterize((ctx) => { ctx.lineWidth = size * 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath(); d.stroke.forEach(([x, y], k) => (k ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.stroke(); });
        applyShape(shape, tool === 'ssub' ? { altKey: true } : { shiftKey: true });
      } else if (Math.abs(x1 - x0) > 2 && Math.abs(y1 - y0) > 2) {
        applyShape(rasterize((ctx) => {
          ctx.beginPath();
          if (tool === 'rect') ctx.rect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));
          else ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, Math.abs(x1 - x0) / 2, Math.abs(y1 - y0) / 2, 0, 0, Math.PI * 2);
          ctx.fill();
        }), d.e);
      }
      redraw();
      return;
    }
    if (!drawing.current) return;
    drawing.current = null;
    if (tool === 'add') { setMarked(Boolean(marksPng(layers.current.marks))); return; }
    if (tool !== 'erase' || !marksPng(layers.current.marks)) save({ paint: paintPng(layers.current.moves, layers.current.still) });
    else setMarked(Boolean(marksPng(layers.current.marks)));
  };
  const undo = useCallback(() => {
    const last = history.current.pop();
    if (!last) return;
    layers.current = last;
    redraw();
    setMarked(Boolean(marksPng(last.marks)));
    save({ paint: paintPng(last.moves, last.still) });
  }, [redraw, save]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
      if (e.key === 'Escape') { if (polygonRef.current.length) { setPolygon([]); return; } onClose(); }
      if (e.key === 'Enter' && polygonRef.current.length > 2) { e.preventDefault(); closePolygonRef.current?.(); }
      if (e.key === '[') setSize((v) => Math.max(4, v - 4));
      if (e.key === ']') setSize((v) => Math.min(140, v + 4));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, onClose]);

  const closePolygon = (e) => {
    if (polygon.length < 3) { setPolygon([]); return; }
    applyShape(rasterize((ctx) => { ctx.beginPath(); polygon.forEach(([x, y], k) => (k ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.fill(); }), e);
    setPolygon([]);
  };
  const addExact = async () => {
    if (!selCount) return;
    setBusy('exact');
    try {
      const next = await api.addPlatePeopleExact(plate.id, { mask: selectionPng(), label: exactLabel, fill: fillWay, hint: behindHint });
      setData(next);
      selection.current = new Uint8Array(FW * FH); selHistory.current = []; tintSelection(); setExactLabel('');
      onChanged?.();
    } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const fillWith = async (method) => {
    setBusy(`fill-${method}`);
    try { setData(await api.fillPlatePeople(plate.id, { method, hint: behindHint })); onChanged?.(); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const uploadClean = async (file) => {
    if (!file) return;
    setBusy('clean');
    try { setData(await api.uploadPlatePeopleClean(plate.id, file)); onChanged?.(); } catch (e) { showError(e); } finally { setBusy(''); if (cleanFile.current) cleanFile.current.value = ''; }
  };

  const clearBrush = () => {
    snapshot();
    layers.current.moves = blank(); layers.current.still = blank();
    redraw();
    save({ paint: null });
  };
  const addOne = async () => {
    const area = marksPng(layers.current.marks);
    if (!area) return;
    setBusy('adding');
    try { setData(await api.addPlatePeopleSubject(plate.id, { area, label })); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const fill = async () => {
    setBusy('filling');
    try { setData(await api.fillPlatePeople(plate.id, { method: 'ai', hint: behindHint })); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const removeOne = async (id) => {
    try { setData(await api.removePlatePeopleSubject(plate.id, id)); onChanged?.(); } catch (e) { showError(e); }
  };
  const dismiss = async () => {
    try { setData(await api.clearPlatePeopleTask(plate.id)); } catch (e) { showError(e); }
  };

  const polygonRef = useRef([]);
  polygonRef.current = polygon;
  const closePolygonRef = useRef(null);
  closePolygonRef.current = () => closePolygon();
  const source = view === 'behind'
    ? api.plateTemplateUrl(plate.id, 'people-clean.jpg', `c${data?.rev || 0}`)
    : api.plateTemplateUrl(plate.id, 'source.jpg', plate.updatedAt);
  const cursor = tool === 'pick' ? 'pointer' : tool === 'wand' ? 'cell' : 'crosshair';

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-gray-50 flex flex-col" role="dialog" aria-modal="true" aria-label={t('plateTemplates.people.editor.title')}>
      <header className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-gray-900">{t('plateTemplates.people.editor.title')}</h2>
          <p className="text-[11px] text-gray-500">{t('plateTemplates.people.editor.hint')}</p>
        </div>
        {busy === 'saving' && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.people.editor.saving')}</span>}
        <button type="button" onClick={onClose} className="btn btn-primary btn-sm flex items-center gap-1"><FiCheck size={14} /> {t('plateTemplates.people.editor.done')}</button>
        <button type="button" onClick={onClose} aria-label={t('plateTemplates.close')} className="btn btn-ghost btn-sm px-2"><FiX size={18} /></button>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <main className="flex-1 min-w-0 overflow-y-auto p-4">
          <div className="max-w-5xl mx-auto space-y-2">
            <div role="tablist" className="inline-flex p-0.5 rounded-lg bg-gray-200/70 text-xs font-medium">
              {[['plate', FiImage], ['behind', FiEye], ['motion', FiFilm]].map(([v, Icon]) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 ${view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Icon size={13} /> {t(`plateTemplates.people.editor.view.${v}`)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <FiZoomOut size={13} />
              {ZOOMS.map((z) => <button key={z} type="button" onClick={() => setZoom(z)} className={`px-2 py-0.5 rounded-md border ${zoom === z ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 bg-white'}`}>{z}×</button>)}
              <FiZoomIn size={13} />
              <span className="text-[11px] text-gray-500 ml-1">{t('plateTemplates.people.exact.zoomHint')}</span>
            </div>
            <div className="overflow-auto rounded-xl" style={{ maxHeight: zoom > 1 ? '70vh' : undefined }}>
            <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-sm select-none" style={{ width: `${zoom * 100}%` }}>
              {view === 'motion' ? (
                <PlateLive plateId={plate.id} words={false} look={{ people: { ...(look || {}), enabled: true } }} version={`${plate.updatedAt}|${data?.rev || 0}`} />
              ) : (
                <>
                  <img src={source} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
                  <canvas
                    ref={overlay} width={W} height={H}
                    onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onDoubleClick={(e) => { if (tool === 'poly') closePolygon(e); }}
                    className="absolute inset-0 w-full h-full touch-none" style={{ cursor }}
                  />
                </>
              )}
              {working && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="inline-flex items-center gap-2 rounded-full bg-black/75 text-white text-xs px-3 py-1.5">
                    <FiLoader size={13} className="animate-spin" /> {t(`plateTemplates.people.editor.task.${data.task.kind}`)} <span className="tabular-nums text-white/70">{since(data.task.startedAt, now)}</span>
                  </span>
                </div>
              )}
            </div>
            </div>
            <p className="text-[11px] text-gray-500">{t(view === 'behind' ? 'plateTemplates.people.editor.behindHint' : view === 'motion' ? 'plateTemplates.people.editor.motionHint' : isSelectTool(tool) ? `plateTemplates.people.exact.toolHint.${tool}` : `plateTemplates.people.editor.toolHint.${tool}`)}</p>
            {data?.uncovered && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                <FiAlertTriangle size={14} /> {t('plateTemplates.people.editor.uncovered')}
                <button type="button" onClick={fill} disabled={working || busy === 'filling'} className="btn btn-primary btn-sm text-xs ml-auto">{t('plateTemplates.people.editor.fill')}</button>
              </div>
            )}
            {data?.task?.status === 'failed' && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                <FiAlertTriangle size={14} /> {data.task.error}
                <button type="button" onClick={dismiss} className="underline ml-auto">{t('plateTemplates.close')}</button>
              </div>
            )}
          </div>
        </main>

        <aside className="lg:w-[360px] flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto p-4 space-y-5">
          {!data ? <p className="text-xs text-gray-500 inline-flex items-center gap-1.5"><FiLoader size={12} className="animate-spin" /> {t('plateTemplates.live.loading')}</p> : (
            <>
              <section className="space-y-2 rounded-xl border border-cyan-200 bg-cyan-50/40 p-3">
                <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.exact.title')}</h3>
                <p className="text-[11px] text-gray-600">{t('plateTemplates.people.exact.hint')}</p>
                <div className="grid grid-cols-6 gap-1">
                  {SELECT_TOOLS.map(([id, Icon]) => (
                    <button key={id} type="button" onClick={() => { setTool(id); setPolygon([]); if (view !== 'plate') setView('plate'); }} aria-pressed={tool === id} title={t(`plateTemplates.people.exact.tool.${id}`)} className={`flex flex-col items-center gap-0.5 rounded-lg border px-0.5 py-1.5 text-[9px] ${tool === id ? 'border-cyan-500 bg-cyan-50 text-cyan-800' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'}`}>
                      <Icon size={15} /> {t(`plateTemplates.people.exact.tool.${id}`)}
                    </button>
                  ))}
                </div>
                {!['sadd', 'ssub'].includes(tool) && isSelectTool(tool) && (
                  <div className="flex items-center gap-1 text-[11px]">
                    {['new', 'add', 'sub'].map((m) => <button key={m} type="button" onClick={() => setSelMode(m)} className={`px-2 py-0.5 rounded-full border ${selMode === m ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 bg-white'}`}>{t(`plateTemplates.people.exact.mode.${m}`)}</button>)}
                    <span className="text-[10px] text-gray-500 ml-1">{t('plateTemplates.people.exact.modeKeys')}</span>
                  </div>
                )}
                {tool === 'wand' && (
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 text-xs text-gray-600">{t('plateTemplates.people.exact.tolerance')}
                      <input type="range" min={1} max={80} value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} className="flex-1 accent-cyan-600" />
                      <span className="w-6 tabular-nums text-right">{tolerance}</span>
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={contiguous} onChange={(e) => setContiguous(e.target.checked)} className="rounded border-gray-300" /> {t('plateTemplates.people.exact.contiguous')}</label>
                  </div>
                )}
                {['sadd', 'ssub'].includes(tool) && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">{t('plateTemplates.people.editor.size')}
                    <input type="range" min={2} max={140} value={size} onChange={(e) => setSize(Number(e.target.value))} className="flex-1 accent-cyan-600" />
                    <span className="w-8 tabular-nums text-right">{size}</span>
                  </label>
                )}
                {tool === 'poly' && polygon.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <button type="button" onClick={() => closePolygon()} disabled={polygon.length < 3} className="btn btn-outline btn-sm text-[11px]">{t('plateTemplates.people.exact.closePoly', { count: polygon.length })}</button>
                    <button type="button" onClick={() => setPolygon((pts) => pts.slice(0, -1))} className="btn btn-ghost btn-sm text-[11px]">{t('plateTemplates.people.exact.lastPoint')}</button>
                    <button type="button" onClick={() => setPolygon([])} className="btn btn-ghost btn-sm text-[11px]">{t('plateTemplates.cancel')}</button>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1">
                  <button type="button" onClick={() => picture.current && setSelection(snapEdges(picture.current, selection.current, 8))} disabled={!selCount} title={t('plateTemplates.people.exact.snapHint')} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1"><FiTarget size={12} /> {t('plateTemplates.people.exact.snap')}</button>
                  <button type="button" onClick={() => setSelection(grow(selection.current, FW, FH, 2))} disabled={!selCount} className="btn btn-ghost btn-sm text-[11px] flex items-center gap-1"><FiMaximize2 size={11} /> {t('plateTemplates.people.exact.grow')}</button>
                  <button type="button" onClick={() => setSelection(shrink(selection.current, FW, FH, 2))} disabled={!selCount} className="btn btn-ghost btn-sm text-[11px] flex items-center gap-1"><FiMinimize2 size={11} /> {t('plateTemplates.people.exact.shrink')}</button>
                  <button type="button" onClick={undoSelection} disabled={!selHistory.current.length} className="btn btn-ghost btn-sm text-[11px] flex items-center gap-1"><FiRotateCcw size={11} /> {t('plateTemplates.people.editor.undo')}</button>
                  <button type="button" onClick={() => setSelection(new Uint8Array(FW * FH))} disabled={!selCount} className="btn btn-ghost btn-sm text-[11px]">{t('plateTemplates.people.exact.clear')}</button>
                </div>
                <p className="text-[10px] text-gray-500">{t('plateTemplates.people.exact.count', { count: selCount.toLocaleString() })}</p>
                <input value={exactLabel} onChange={(e) => setExactLabel(e.target.value)} maxLength={60} placeholder={t('plateTemplates.people.editor.labelPlaceholder')} className="input text-xs" />
                <div className="space-y-1">
                  <p className="text-[11px] text-gray-600">{t('plateTemplates.people.exact.fillTitle')}</p>
                  <div className="flex flex-wrap gap-1 text-[11px]">
                    {['extend', 'ai', 'none'].map((m) => <button key={m} type="button" onClick={() => setFillWay(m)} className={`px-2 py-0.5 rounded-full border ${fillWay === m ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 bg-white'}`}>{t(`plateTemplates.people.exact.fill.${m}`)}</button>)}
                  </div>
                  {fillWay === 'ai' && <input value={behindHint} onChange={(e) => setBehindHint(e.target.value)} maxLength={300} placeholder={t('plateTemplates.people.exact.behindPlaceholder')} className="input text-xs" />}
                  <p className="text-[10px] text-gray-500">{t(`plateTemplates.people.exact.fillHint.${fillWay}`)}</p>
                </div>
                <button type="button" onClick={addExact} disabled={!selCount || working || busy === 'exact'} className="btn btn-primary btn-sm w-full flex items-center justify-center gap-1">
                  {busy === 'exact' ? <FiLoader size={13} className="animate-spin" /> : <FiPlus size={13} />} {t('plateTemplates.people.exact.add')}
                </button>
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.tools')}</h3>
                <div className="grid grid-cols-5 gap-1">
                  {TOOLS.map(([id, Icon]) => (
                    <button key={id} type="button" onClick={() => { setTool(id); if (view === 'motion') setView('plate'); }} aria-pressed={tool === id} title={t(`plateTemplates.people.editor.tool.${id}`)} className={`flex flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5 text-[10px] ${tool === id ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                      <Icon size={15} /> {t(`plateTemplates.people.editor.tool.${id}`)}
                    </button>
                  ))}
                </div>
                {tool !== 'pick' && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    {t('plateTemplates.people.editor.size')}
                    <input type="range" min={4} max={140} value={size} onChange={(e) => setSize(Number(e.target.value))} className="flex-1 accent-primary-600" />
                    <span className="w-8 tabular-nums text-right">{size}</span>
                  </label>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={undo} disabled={!history.current.length} className="btn btn-ghost btn-sm text-xs flex items-center gap-1"><FiRotateCcw size={12} /> {t('plateTemplates.people.editor.undo')}</button>
                  {data.painted && <button type="button" onClick={clearBrush} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.people.editor.clearBrush')}</button>}
                </div>
              </section>

              {tool === 'add' && (
                <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.addTitle')}</h3>
                  <p className="text-[11px] text-gray-600">{t('plateTemplates.people.editor.addHint')}</p>
                  <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder={t('plateTemplates.people.editor.labelPlaceholder')} className="input text-sm" />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={addOne} disabled={!marked || working || busy === 'adding'} className="btn btn-primary btn-sm flex items-center gap-1">
                      {busy === 'adding' ? <FiLoader size={13} className="animate-spin" /> : <FiSearch size={13} />} {t('plateTemplates.people.editor.addGo')}
                    </button>
                    {marked && <button type="button" onClick={() => { snapshot(); layers.current.marks = blank(); setMarked(false); redraw(); }} className="btn btn-ghost btn-sm text-xs">{t('plateTemplates.people.editor.clearMarks')}</button>}
                  </div>
                  <p className="text-[10px] text-gray-500">{t('plateTemplates.people.editor.addCost')}</p>
                </section>
              )}

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.editor.list', { moving: data.subjects.filter((s) => s.selected).length, count: data.subjects.length })}</h3>
                <ul className="space-y-1">
                  {data.subjects.map((s, k) => (
                    <li key={s.id} className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
                      <input type="checkbox" checked={s.selected} onChange={() => toggle(s.id)} disabled={working} className="rounded border-gray-300" aria-label={t('plateTemplates.people.editor.moves', { n: k + 1 })} />
                      <span className="w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center" style={{ background: s.selected ? HUES[k % HUES.length] : '#9CA3AF' }}>{k + 1}</span>
                      <span className="flex-1 text-xs text-gray-700 truncate">
                        {s.label || t(s.kind === 'added' ? 'plateTemplates.people.editor.object' : 'plateTemplates.people.editor.person', { n: k + 1 })}
                        {s.rough && <span className="ml-1 text-amber-700">· {t('plateTemplates.people.editor.rough')}</span>}
                      </span>
                      <span className="text-[10px] text-gray-500">{t(s.selected ? 'plateTemplates.people.editor.moving' : 'plateTemplates.people.editor.still')}</span>
                      {data.subjects.length > 1 && <button type="button" onClick={() => removeOne(s.id)} disabled={working} title={t('plateTemplates.remove')} aria-label={t('plateTemplates.remove')} className="text-gray-400 hover:text-red-600 px-1"><FiTrash2 size={12} /></button>}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-gray-500">{t('plateTemplates.people.editor.listHint')}</p>
              </section>

              {data.subjects.length > 0 && (
                <section className="space-y-2 rounded-xl border border-gray-200 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t('plateTemplates.people.exact.behindTitle')}</h3>
                  <p className="text-[11px] text-gray-600">{t('plateTemplates.people.exact.behindHint')}</p>
                  <input value={behindHint} onChange={(e) => setBehindHint(e.target.value)} maxLength={300} placeholder={t('plateTemplates.people.exact.behindPlaceholder')} className="input text-xs" />
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => fillWith('extend')} disabled={working || Boolean(busy)} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1">{busy === 'fill-extend' ? <FiLoader size={12} className="animate-spin" /> : <FiMaximize2 size={12} />} {t('plateTemplates.people.exact.fill.extend')}</button>
                    <button type="button" onClick={() => fillWith('ai')} disabled={working || Boolean(busy)} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1">{busy === 'fill-ai' ? <FiLoader size={12} className="animate-spin" /> : <FiZap size={12} />} {t('plateTemplates.people.exact.fill.ai')}</button>
                    <input ref={cleanFile} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadClean(e.target.files?.[0])} />
                    <button type="button" onClick={() => cleanFile.current?.click()} disabled={working || Boolean(busy)} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1">{busy === 'clean' ? <FiLoader size={12} className="animate-spin" /> : <FiUpload size={12} />} {t('plateTemplates.people.exact.cleanUpload')}</button>
                  </div>
                  <button type="button" onClick={() => setView('behind')} className="text-[11px] text-primary-700 underline">{t('plateTemplates.people.exact.seeBehind')}</button>
                  {data.cleanUploaded && <p className="text-[10px] text-green-700">{t('plateTemplates.people.exact.cleanUsed')}</p>}
                </section>
              )}
            </>
          )}
        </aside>
      </div>
    </div>,
    document.body,
  );
};

export default PeopleEditor;
